import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '@/pages/painel-pedidos/lib/errors';
import { FONTE_TIPOS, type Fonte } from '@/pages/painel/lib/radarConstants';

interface Props {
  fonte: Fonte | null;
  password: string;
  onClose: () => void;
  onSaved: () => void;
}

export default function RadarFonteFormModal({ fonte, password, onClose, onSaved }: Props) {
  const [nome, setNome] = useState(fonte?.nome ?? '');
  const [tipo, setTipo] = useState(fonte?.tipo ?? 'manual');
  const [url, setUrl] = useState(fonte?.url ?? '');
  const [observacao, setObservacao] = useState(fonte?.observacao ?? '');
  const [status, setStatus] = useState(fonte?.status ?? 'ativo');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const salvar = async () => {
    if (!nome.trim()) {
      setError('O nome da fonte é obrigatório.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('radar-admin', {
        body: {
          action: 'fonte_salvar',
          password,
          fonte_id: fonte?.id ?? null,
          nome: nome.trim(),
          tipo,
          url: url.trim(),
          observacao: observacao.trim(),
          status,
        },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = data as { error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao salvar fonte.');
    } finally {
      setSaving(false);
    }
  };

  const fieldCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 bg-white focus:outline-none focus:border-[#E8610A]';
  const labelCls = 'block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-gray-200 w-full max-w-lg max-h-[92vh] flex flex-col shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between shrink-0">
          <div>
            <h3 className="font-bold text-gray-900 text-base">{fonte ? 'Editar fonte' : 'Nova fonte'}</h3>
            <p className="text-xs text-gray-500">Registre de onde a informação costuma vir.</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer">
            <i className="ri-close-line"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div>
            <label className={labelCls}>Nome da fonte *</label>
            <input type="text" value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Ex: Diário de Pernambuco" className={fieldCls} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Tipo da fonte</label>
              <select value={tipo} onChange={(e) => setTipo(e.target.value)} className={fieldCls}>
                {FONTE_TIPOS.map((t) => (
                  <option key={t.key} value={t.key}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className={fieldCls}>
                <option value="ativo">Ativo</option>
                <option value="inativo">Inativo</option>
              </select>
            </div>
          </div>

          <div>
            <label className={labelCls}>URL / base da fonte</label>
            <input type="text" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://..." className={fieldCls} />
          </div>

          <div>
            <label className={labelCls}>Observação</label>
            <textarea value={observacao} onChange={(e) => setObservacao(e.target.value)} rows={2} placeholder="Ex: importação CAU-PE" className={`${fieldCls} resize-y`} />
          </div>

          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2 shrink-0 bg-white">
          <button onClick={onClose} className="inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer whitespace-nowrap">
            Cancelar
          </button>
          <button
            onClick={salvar}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-5 py-2 text-sm font-semibold text-white bg-[#E8610A] hover:bg-[#C4500A] rounded-lg cursor-pointer whitespace-nowrap disabled:opacity-60"
          >
            {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
}